import { SlipOutcome } from '../services/api';
import { profitPer100 } from './parlay';

/** A bet as the slip maths sees it: American odds and a stake in dollars (0 = not placed). */
export interface PricedBet {
  odds: number;
  stake: number;
}

export interface SlipStats {
  /** Expected profit in dollars, and the chance the whole slip finishes ahead. */
  ev: number;
  pProfit: number;
  /** 10th, 50th and 90th percentile of profit: the "8 races in 10" range and the typical result. */
  p10: number;
  p50: number;
  p90: number;
  worst: number;
  best: number;
  /** Chance every placed bet loses. */
  pAllLose: number;
  staked: number;
}

const totalSims = (outcomes: SlipOutcome[]) => outcomes.reduce((t, o) => t + o.sims, 0);
const hit = (mask: number, b: number) => (mask & (1 << b)) !== 0;

/** Each bet's chance of hitting. */
export const betProbs = (outcomes: SlipOutcome[], nBets: number): number[] => {
  const total = totalSims(outcomes) || 1;
  return Array.from(
    { length: nBets },
    (_, b) => outcomes.reduce((t, o) => t + (hit(o.hitMask, b) ? o.sims : 0), 0) / total,
  );
};

/** Profit or loss on the whole slip when exactly the bets in `mask` hit. */
export const netForMask = (mask: number, bets: PricedBet[]): number =>
  bets.reduce(
    (t, b, i) => (b.stake > 0 ? t + (hit(mask, i) ? (b.stake * profitPer100(b.odds)) / 100 : -b.stake) : t),
    0,
  );

const quantile = (sorted: { v: number; w: number }[], q: number, total: number) => {
  let c = 0;
  for (const x of sorted) {
    c += x.w;
    if (c >= q * total) return x.v;
  }
  return sorted.length ? sorted[sorted.length - 1].v : 0;
};

export const slipStats = (outcomes: SlipOutcome[], bets: PricedBet[]): SlipStats => {
  const total = totalSims(outcomes) || 1;
  const rows = outcomes.map((o) => ({ v: netForMask(o.hitMask, bets), w: o.sims })).sort((a, b) => a.v - b.v);
  const staked = bets.reduce((t, b) => t + Math.max(0, b.stake), 0);
  const placed = bets.map((b, i) => (b.stake > 0 ? i : -1)).filter((i) => i >= 0);
  return {
    ev: rows.reduce((t, r) => t + r.v * r.w, 0) / total,
    pProfit: rows.reduce((t, r) => t + (r.v > 1e-9 ? r.w : 0), 0) / total,
    p10: quantile(rows, 0.1, total),
    p50: quantile(rows, 0.5, total),
    p90: quantile(rows, 0.9, total),
    worst: rows.length ? rows[0].v : 0,
    best: rows.length ? rows[rows.length - 1].v : 0,
    pAllLose:
      placed.length === 0
        ? 0
        : outcomes.reduce((t, o) => t + (placed.every((i) => !hit(o.hitMask, i)) ? o.sims : 0), 0) / total,
    staked,
  };
};

/** Share of simulated races in each profit bin; `edges` are the bin boundaries in dollars. */
export const profitHistogram = (outcomes: SlipOutcome[], bets: PricedBet[], edges: number[]): number[] => {
  const total = totalSims(outcomes) || 1;
  const out = new Array(edges.length - 1).fill(0);
  outcomes.forEach((o) => {
    const v = netForMask(o.hitMask, bets);
    let i = edges.findIndex((e, k) => k < edges.length - 1 && v >= e && v < edges[k + 1]);
    if (i < 0) i = v < edges[0] ? 0 : edges.length - 2;
    out[i] += o.sims / total;
  });
  return out;
};

/** Round-number bin edges covering every outcome of the given slips (about `target` bins). */
export const histogramEdges = (outcomes: SlipOutcome[], slips: PricedBet[][], target = 28): number[] => {
  const vals = slips.flatMap((bets) => outcomes.map((o) => netForMask(o.hitMask, bets)));
  const lo = Math.min(0, ...vals);
  const hi = Math.max(0, ...vals);
  const raw = (hi - lo) / target || 1;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? raw;
  const start = Math.floor(lo / step) * step;
  const edges: number[] = [];
  for (let e = start; e <= hi + step; e += step) edges.push(Number(e.toFixed(6)));
  return edges;
};

/** Correlation of two bets' hits across the simulated races (phi coefficient); 0 when either never varies. */
export const hitCorrelation = (outcomes: SlipOutcome[], a: number, b: number): number => {
  const total = totalSims(outcomes) || 1;
  let pa = 0;
  let pb = 0;
  let pab = 0;
  outcomes.forEach((o) => {
    const ha = hit(o.hitMask, a);
    const hb = hit(o.hitMask, b);
    if (ha) pa += o.sims;
    if (hb) pb += o.sims;
    if (ha && hb) pab += o.sims;
  });
  pa /= total;
  pb /= total;
  pab /= total;
  const den = Math.sqrt(pa * (1 - pa) * pb * (1 - pb));
  return den > 0 ? (pab - pa * pb) / den : 0;
};

/** The k most common outcomes, with their chance. */
/**
 * The k most common results, with their chance. With `bets`, outcomes that differ only in bets with no stake are
 * merged (they pay the same), and each result carries its profit.
 */
export const likelyOutcomes = (
  outcomes: SlipOutcome[],
  k = 4,
  bets?: PricedBet[],
): { mask: number; prob: number; net: number }[] => {
  const total = totalSims(outcomes) || 1;
  const staked = bets ? bets.reduce((m, b, i) => (b.stake > 0 ? m | (1 << i) : m), 0) : -1;
  const merged = new Map<number, number>();
  outcomes.forEach((o) => {
    const m = o.hitMask & staked;
    merged.set(m, (merged.get(m) ?? 0) + o.sims);
  });
  return Array.from(merged.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, k)
    .map(([mask, sims]) => ({ mask, prob: sims / total, net: bets ? netForMask(mask, bets) : 0 }));
};

const roundDown = (v: number, step: number) => Math.floor(v / step + 1e-9) * step;

/**
 * Exposure groups: bets that win and lose together count as one exposure. Two bets are linked when their hits are
 * strongly correlated (>= 0.5), or when they back the same driver (`related`) and are at least mildly correlated
 * (>= 0.2: "Larson top 5" and "Larson ahead of Briscoe" both ride on Larson running well). Links chain, so a group can
 * hold several bets. Returns a group id per bet.
 */
export const exposureGroups = (
  outcomes: SlipOutcome[],
  nBets: number,
  related: (a: number, b: number) => boolean = () => false,
): number[] => {
  const parent = Array.from({ length: nBets }, (_, i) => i);
  const find = (i: number): number => (parent[i] === i ? i : (parent[i] = find(parent[i])));
  for (let a = 0; a < nBets; a++)
    for (let b = a + 1; b < nBets; b++) {
      const r = hitCorrelation(outcomes, a, b);
      if (r >= 0.5 || (r >= 0.2 && related(a, b))) parent[find(b)] = find(a);
    }
  return parent.map((_, i) => find(i));
};

/** Money on each exposure group, and the biggest one: { group, amount, share of the stakes }. */
export const biggestExposure = (stakes: number[], groups: number[]) => {
  const byGroup = new Map<number, number>();
  stakes.forEach((v, i) => v > 0 && byGroup.set(groups[i], (byGroup.get(groups[i]) ?? 0) + v));
  const total = stakes.reduce((t, v) => t + Math.max(0, v), 0);
  let best: { group: number; amount: number; share: number } | null = null;
  byGroup.forEach((amount, group) => {
    if (!best || amount > best.amount) best = { group, amount, share: total > 0 ? amount / total : 0 };
  });
  return best as { group: number; amount: number; share: number } | null;
};

/** No exposure group above `limit` dollars in total. */
const withinGroupCaps = (stakes: number[], groups: number[], limit: number): boolean => {
  const byGroup = new Map<number, number>();
  for (let i = 0; i < stakes.length; i++) {
    if (!stakes[i]) continue;
    const t = (byGroup.get(groups[i]) ?? 0) + stakes[i];
    if (t > limit + 1e-9) return false;
    byGroup.set(groups[i], t);
  }
  return true;
};

/**
 * "Max value": up to the same budget over the value bets for the most expected profit, with no bet above `cap` of
 * the budget. Expected profit is linear in the stakes, so the best bets fill up to the cap in order; whatever the
 * cap leaves over stays unbet rather than piling onto one bet.
 */
export const maxValueStakes = (
  outcomes: SlipOutcome[],
  bets: PricedBet[],
  value: boolean[],
  budget: number,
  cap = 0.4,
  groups: number[] = bets.map((_, i) => i),
  /** How good each bet is, best first (e.g. bankroll growth on the cautious probability, as the board scanner ranks
   * bets). Defaults to expected profit per dollar, which favors long prices. Bets scoring 0 or less get nothing. */
  score?: number[],
): number[] => {
  const probs = betProbs(outcomes, bets.length);
  const stakes = new Array(bets.length).fill(0);
  const idx = bets.map((_, i) => i).filter((i) => value[i]);
  if (idx.length === 0 || budget <= 0) return stakes;
  const step = budget >= 50 ? 5 : 1;
  const perDollar = (i: number) => (probs[i] * profitPer100(bets[i].odds)) / 100 - (1 - probs[i]);
  const limit = roundDown(cap * budget, step);
  // The cap is per exposure group: bets that win and lose together share one limit.
  const groupLeft = new Map<number, number>();
  let left = budget;
  const rank = (i: number) => (score ? score[i] : perDollar(i));
  [...idx]
    .filter((i) => rank(i) > 0 && perDollar(i) > 0)
    .sort((a, b) => rank(b) - rank(a) || perDollar(b) - perDollar(a))
    .forEach((i) => {
      const room = groupLeft.get(groups[i]) ?? limit;
      const s = Math.min(room, roundDown(left, step));
      stakes[i] = s;
      left -= s;
      groupLeft.set(groups[i], room - s);
    });
  return stakes;
};

/**
 * Local search over stakes (each at most `cap` of the budget, total at most the budget) on the `eligible` bets,
 * maximizing `objective` while expecting at least `minEv`.
 */
const searchStakes = (
  outcomes: SlipOutcome[],
  bets: PricedBet[],
  value: boolean[],
  budget: number,
  minEv: number,
  objective: (st: SlipStats) => number,
  cap: number,
  groups: number[],
): number[] => {
  const idx = bets.map((_, i) => i).filter((i) => value[i]);
  const zero = new Array(bets.length).fill(0);
  if (idx.length === 0 || budget <= 0) return zero;
  const step = budget >= 50 ? 5 : 1;
  const limit = roundDown(cap * budget, step);
  const score = (stakes: number[]) => {
    const total = stakes.reduce((t, v) => t + v, 0);
    if (total <= 0 || !withinGroupCaps(stakes, groups, limit)) return -Infinity;
    const st = slipStats(
      outcomes,
      bets.map((b, i) => ({ ...b, stake: stakes[i] })),
    );
    return st.ev + 1e-9 < minEv ? -Infinity : objective(st);
  };
  // Chance of profit moves in jumps (a bet's win either covers the others' losses or it doesn't), so small steps from a
  // single start can stall on a plateau. Start from several shapes (the max-value stakes, each value bet alone at the
  // cap, an even split) and also allow dropping a bet entirely; keep the best.
  const starts: number[][] = [maxValueStakes(outcomes, bets, value, budget, cap, groups)];
  idx.forEach((i) => {
    const st = [...zero];
    st[i] = limit;
    starts.push(st);
  });
  const even = roundDown(Math.min(limit, budget / idx.length), step);
  const evenSplit = bets.map((_, i) => (value[i] ? even : 0));
  if (even > 0 && withinGroupCaps(evenSplit, groups, limit)) starts.push(evenSplit);
  const climb = (start: number[]) => {
    let cur = start;
    let curScore = score(cur);
    for (let iter = 0; iter < 400; iter++) {
      let improved = false;
      const total = cur.reduce((t, v) => t + v, 0);
      const candidates: number[][] = [];
      for (const a of idx) {
        if (cur[a] > 0) candidates.push(cur.map((v, k) => (k === a ? 0 : v))); // drop a bet entirely
        if (cur[a] >= step) candidates.push(cur.map((v, k) => (k === a ? v - step : v))); // take a step off
        if (total + step <= budget && cur[a] + step <= limit)
          candidates.push(cur.map((v, k) => (k === a ? v + step : v)));
        for (const b of idx)
          if (a !== b && cur[a] >= step && cur[b] + step <= limit)
            candidates.push(cur.map((v, k) => (k === a ? v - step : k === b ? v + step : v)));
      }
      for (const next of candidates) {
        const sc = score(next);
        if (sc > curScore + 1e-9) {
          cur = next;
          curScore = sc;
          improved = true;
        }
      }
      if (!improved) break;
    }
    return { cur, curScore };
  };
  let best = { cur: zero, curScore: -Infinity };
  starts.forEach((st) => {
    const r = climb(st);
    if (r.curScore > best.curScore) best = r;
  });
  const { cur, curScore } = best;
  return curScore === -Infinity ? zero : cur;
};

/**
 * "Steadier": the best chance of finishing ahead while expecting at least `minEv` (then the most value).
 * `eligible` can include bets that are too close to call: a likely bet with a near-zero edge is what steadies a slip.
 */
export const steadierStakes = (
  outcomes: SlipOutcome[],
  bets: PricedBet[],
  eligible: boolean[],
  budget: number,
  minEv: number,
  cap = 0.4,
  groups: number[] = bets.map((_, i) => i),
): number[] => searchStakes(outcomes, bets, eligible, budget, minEv, (st) => st.pProfit * 1e6 + st.ev, cap, groups);

/**
 * "Balanced": the best typical result (median profit: what you'd most often walk away with) while expecting at least
 * `minEv`, then the most value. Balances edge against how often the bets hit.
 */
export const balancedStakes = (
  outcomes: SlipOutcome[],
  bets: PricedBet[],
  eligible: boolean[],
  budget: number,
  minEv: number,
  cap = 0.4,
  groups: number[] = bets.map((_, i) => i),
): number[] => searchStakes(outcomes, bets, eligible, budget, minEv, (st) => st.p50 * 1e3 + st.ev, cap, groups);

/** American odds for a probability, rounded the way books quote them (5s under 300, 10s under 1000, 50s above). */
export const americanForProb = (p: number): number | null => {
  if (!(p > 0 && p < 1)) return null;
  const raw = p >= 0.5 ? (100 * p) / (1 - p) : (100 * (1 - p)) / p;
  const step = raw < 300 ? 5 : raw < 1000 ? 10 : 50;
  const v = Math.ceil(raw / step) * step;
  return p >= 0.5 ? -v : v;
};

/**
 * The same outcomes with the bets renumbered: `to[b]` is bet b's new position. Used when bets are reordered after a
 * check, so the result still lines up without pricing the slip again.
 */
export const remapMasks = (outcomes: SlipOutcome[], to: number[]): SlipOutcome[] => {
  const remap = (mask: number) => to.reduce((m, p, b) => (mask & (1 << b) ? m | (1 << p) : m), 0);
  return outcomes.map((o) => ({ ...o, hitMask: remap(o.hitMask) }));
};

/** Dollars: whole amounts as "$15", anything with cents as "$15.50" (thousands separated). */
export const dollars = (v: number) => {
  const cents = Math.round(Math.abs(v) * 100);
  const digits = cents % 100 === 0 ? 0 : 2;
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
};
/** Signed dollars for profit and loss: "+$32.23", "−$87", "$0". */
export const money = (v: number) => {
  const cents = Math.round(v * 100);
  return `${cents < 0 ? '−' : cents > 0 ? '+' : ''}${dollars(v)}`;
};
export const pct = (v: number) => `${Math.round(v * 100)}%`;

/** Share of simulated races where the slip at these stakes makes at least `amount`. */
export const chanceOfAtLeast = (outcomes: SlipOutcome[], bets: PricedBet[], amount: number): number => {
  const total = totalSims(outcomes) || 1;
  return outcomes.reduce((t, o) => t + (netForMask(o.hitMask, bets) >= amount - 1e-9 ? o.sims : 0), 0) / total;
};

/**
 * Ways to stake the `eligible` bets with up to `budget` in total and no exposure group above `cap` of it, on a dollar
 * grid. Every combination when there are few enough (up to `limit`), otherwise a random sample of them (plus each bet
 * alone at the cap and an even split), so the Risk and Goals views stay quick on big slips. Seeded, so the same slip
 * gives the same candidates.
 */
export const stakeCandidates = (
  bets: PricedBet[],
  eligible: boolean[],
  budget: number,
  groups: number[],
  cap = 0.4,
  limit = 20000,
): number[][] => {
  const idx = bets.map((_, i) => i).filter((i) => eligible[i]);
  if (idx.length === 0 || budget <= 0) return [];
  const limitAmt = cap * budget;
  // A grid step that keeps about 7 stake levels per bet (whole $5s on budgets of $50 or more).
  const base = budget >= 50 ? 5 : 1;
  const step = Math.max(base, Math.ceil(limitAmt / 6 / base) * base);
  const levels: number[] = [];
  for (let v = 0; v <= limitAmt + 1e-9; v += step) levels.push(v);
  const ok = (st: number[]) => {
    const total = st.reduce((t, v) => t + v, 0);
    if (total <= 0 || total > budget + 1e-9) return false;
    const byGroup = new Map<number, number>();
    for (let i = 0; i < st.length; i++) {
      if (!st[i]) continue;
      const g = (byGroup.get(groups[i]) ?? 0) + st[i];
      if (g > limitAmt + 1e-9) return false;
      byGroup.set(groups[i], g);
    }
    return true;
  };
  const zero = bets.map(() => 0);
  const out: number[][] = [];
  const seen = new Set<string>();
  const push = (st: number[]) => {
    const k = st.join();
    if (!seen.has(k) && ok(st)) {
      seen.add(k);
      out.push(st);
    }
  };
  if (Math.pow(levels.length, idx.length) <= limit) {
    const cur = [...zero];
    const rec = (k: number) => {
      if (k === idx.length) return push([...cur]);
      for (const v of levels) {
        cur[idx[k]] = v;
        rec(k + 1);
      }
      cur[idx[k]] = 0;
    };
    rec(0);
    return out;
  }
  let seed = 12345;
  const rand = () => (seed = (seed * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff;
  idx.forEach((i) => push(zero.map((_, k) => (k === i ? levels[levels.length - 1] : 0))));
  push(
    zero.map((_, k) =>
      eligible[k] ? levels[Math.min(levels.length - 1, Math.max(1, Math.floor(budget / idx.length / step)))] : 0,
    ),
  );
  for (let n = 0; n < limit * 3 && out.length < limit; n++) {
    const keep = 0.25 + rand() * 0.75; // sometimes only a few bets, sometimes most of them
    push(zero.map((_, k) => (eligible[k] && rand() < keep ? levels[Math.floor(rand() * levels.length)] : 0)));
  }
  return out;
};

/** The best trade-offs: from the most expected profit down, each slip with a higher chance of profit than the last. */
export const riskFrontier = <T extends { stats: SlipStats }>(cands: T[]): T[] => {
  const sorted = [...cands].sort((a, b) => b.stats.ev - a.stats.ev || b.stats.pProfit - a.stats.pProfit);
  const out: T[] = [];
  let best = -1;
  for (const c of sorted)
    if (c.stats.pProfit > best + 0.004) {
      out.push(c);
      best = c.stats.pProfit;
    }
  return out;
};
